import { AimdConcurrencyController, processWithAdaptiveConcurrency } from "./concurrency";
import type { ChunkSourceFactory, DriverChunkMeta, DriverFile, MultipartSessionShape, UploadLease, UploadTelemetry } from "./ports";
import {
	uploadChunk,
	ChunkNetworkError,
	MissingEtagError,
 S3HttpError,
	PersistedByteIntegrityError,
	UploadCancelledError,
	UploadOwnershipLostError,
	type ChunkSourceFailureKind,
	type S3ChunkTransport
} from "./uploadChunk";
import { isValidEtag } from "./multipartValidation";
import { runGuardedEffect } from "./ownershipGate";
import { pinnedUploadsTargetIdForPart, type PartReceiptRecoveryPort } from "./partReceiptRecovery";
import { isConfirmedMigrationIntegrityError } from "./driverUrlRefresh";

export interface UploadedPart {
	ETag: string;
	PartNumber: number;
}

type ChunkLoopMeta = Pick<DriverChunkMeta, "chunkIndex" | "size" | "isUploaded" | "etag">;

export function createUploadedChunkBytes(allChunks: readonly ChunkLoopMeta[], totalChunks: number): number[] {
	const chunkBytesUploaded = new Array<number>(totalChunks).fill(0);
	for (const chunk of allChunks) {
		if (chunk.isUploaded && isValidEtag(chunk.etag)) {
			chunkBytesUploaded[chunk.chunkIndex] = chunk.size;
		}
	}
	return chunkBytesUploaded;
}

export function validatePersistedChunkByteIntegrity(
	session: Pick<MultipartSessionShape, "fileSize" | "totalChunks">,
	allChunks: readonly Pick<ChunkLoopMeta, "chunkIndex" | "size">[]
): void {
	const chunkSizesByIndex = new Map(
		allChunks
			.filter((chunk) => Number.isInteger(chunk.chunkIndex) && chunk.chunkIndex >= 0 && chunk.chunkIndex < session.totalChunks)
			.map((chunk) => [chunk.chunkIndex, chunk.size])
	);
	const persistedByteTotal = [...chunkSizesByIndex.values()].reduce((totalBytes, chunkBytes) => totalBytes + chunkBytes, 0);
	// A zero-byte chunk is normally proof of lost bytes, but for a file whose OWN fileSize is 0 it is
	// the whole (empty) file recorded faithfully — which is exactly what a legacy resume's fallback
	// derives (`normalizeLegacyResumeChunkMetadata`'s `Math.ceil(0 / totalChunks)` is honestly 0). The
	// carve-out is scoped to that single case and nothing else: a zero-byte chunk in a file that is
	// supposed to hold bytes still fails here, and even at fileSize 0 a negative/non-integer size or a
	// grid whose bytes do not total fileSize still fails on the checks either side of it (round 80 /
	// LSP-17735).
	const minimumChunkBytes = session.fileSize === 0 ? 0 : 1;
	if (
		chunkSizesByIndex.size !== session.totalChunks ||
		[...chunkSizesByIndex.values()].some((chunkBytes) => !Number.isSafeInteger(chunkBytes) || chunkBytes < minimumChunkBytes) ||
		persistedByteTotal !== session.fileSize
	) {
		// TruncatedPartsError (not a bare Error) — this condition is a property of bytes that no
		// longer exist anywhere (a short read on a chunk source, recorded verbatim by
		// persistChunkFromSource), so no retry can ever change it. contact-admin's classifyFailure
		// already special-cases `instanceof TruncatedPartsError` as "terminal-local-gaps"; a bare
		// Error fell through to the generic retryable "other" classification in both apps, spinning
		// the drain on an unfixable session forever instead of reaching the truthful terminal
		// bytes-gone/needs-attention verdict round 50's sibling check already reaches for the
		// identical condition (round 51 / LSP-17735). PersistedByteIntegrityError (a TruncatedPartsError
		// SUBCLASS, so round 51's classification is untouched) so the resume catch can tell this
		// LOCAL-bytes verdict apart from the post-upload "have N/M part etags" refusal and keep it away
		// from the listParts-driven handoff decision (round 67 / LSP-17735 — see the class doc).
		throw new PersistedByteIntegrityError(
			`Refusing multipart upload: persisted chunk sizes total ${persistedByteTotal} bytes; expected ${session.fileSize}`
		);
	}
}

interface UploadPendingChunksOptions<Session extends MultipartSessionShape> {
	session: Session;
	allChunks: readonly ChunkLoopMeta[];
	pendingChunks: readonly ChunkLoopMeta[];
	chunkUrls: readonly string[];
	chunkHeaders?: readonly Readonly<Record<string, string>>[];
	parts: UploadedPart[];
	chunkSources: Pick<ChunkSourceFactory<DriverFile, Session>, "forPersistedUpload">;
	transport: S3ChunkTransport;
	lease: UploadLease<Session>;
	telemetry: UploadTelemetry;
	chunkSize: number;
	chunkConcurrency: number;
	maxChunkRetries: number;
	cancelSignal?: AbortSignal;
	refreshPresignedUrl?: (
		chunkIndex: number,
		expiredUrl?: string,
		pendingChunkIndexes?: readonly number[],
		reason?: "expired" | "network"
	) => Promise<string>;
	checkpointChunk(chunkIndex: number, etag: string): Promise<void>;
	/**
	 * LSP-19201: asks the server for the receipt of a part it ACCEPTED but whose ETag never reached us.
	 * Consulted after a lost ETag or a v2 write-once 412 — see
	 * `partReceiptRecovery.ts` for why re-PUTting cannot repair that failure and why the trigger has to
	 * live here rather than in resume-time reconciliation. Answering `null` leaves the original verdict
	 * untouched. Optional so an out-of-tree consumer of this generic package keeps today's behaviour.
	 */
	recoverPartReceipt?: PartReceiptRecoveryPort;
	createChunkSourceError?: (kind: ChunkSourceFailureKind, fileName: string) => Error;
	onCheckpointError?: (error: unknown, chunkIndex: number, etag: string) => void;
	onLeaseTouchError?: (error: unknown, chunkIndex: number) => void;
	onChunkComplete?: (input: { chunkIndex: number; chunkSize: number; chunksCompleted: number; totalPendingChunks: number }) => void;
	/** Display bytes: saved receipts plus active PUT bytes; may decrease when a PUT retries. */
	onProgress?: (bytesUploaded: number) => void;
	onActivity?: () => void;
	isOnline?: () => boolean;
	waitForOnline?: (signal: AbortSignal) => Promise<void>;
	isPresignedUrlExpired?: (url: string) => boolean;
	retryDelay?: (attempt: number, reason: "retry" | "refresh") => Promise<void>;
	ensureOwnershipAfterRead?: boolean;
}

interface UploadPendingChunksResult {
	outcome: "uploaded" | "yielded";
	chunksCompleted: number;
}

// An active PUT can legitimately outlive admin's writer TTL even when Safari stops emitting byte
// progress. Refresh the lease often enough to stay well inside that window without turning frequent
// XHR progress events (or parallel chunks) into an IndexedDB write for every event. Exported so consuming apps
// can guard-test that their own stale-owner threshold keeps a safe margin over this cadence — see
// apps/contact-admin/tests/unit/db/fileUploadDb.test.ts's
// "keeps the stale-owner reclaim bar beyond progress cadence, stall timeout, and maximum retry backoff".
export const UPLOAD_PROGRESS_LEASE_TOUCH_INTERVAL_MS = 30_000;

/**
 * Shared Step 3 kernel for persisted multipart chunk uploads. Completion runs through shared
 * completionTransition.finalizeMultipartCompletion; apps supply terminal-recovery policy hooks.
 */
export async function uploadPendingChunks<Session extends MultipartSessionShape>(
	options: UploadPendingChunksOptions<Session>
): Promise<UploadPendingChunksResult> {
	const observedChunkIndexes = new Set<number>();
	for (const chunk of options.allChunks) {
		if (observedChunkIndexes.has(chunk.chunkIndex)) {
			// PersistedByteIntegrityError, not a bare Error — same corrupt durable chunk grid as
			// validatePersistedChunkByteIntegrity below (see PersistedByteIntegrityError's own doc). A bare
			// Error fell to the retryable "other" bucket, and no retry can de-duplicate a persisted index.
			throw new PersistedByteIntegrityError(`Refusing multipart upload: duplicate persisted chunk index ${chunk.chunkIndex}`);
		}
		observedChunkIndexes.add(chunk.chunkIndex);
	}
	validatePersistedChunkByteIntegrity(options.session, options.allChunks);
	const chunkBytesUploaded = createUploadedChunkBytes(options.allChunks, options.session.totalChunks);
	let totalBytesUploaded = chunkBytesUploaded.reduce((totalBytes, chunkBytes) => totalBytes + chunkBytes, 0);
	// Live bytes are display-only. Receipts, checkpoints and adaptive concurrency keep using
	// the durable counters below, even when an entire request body has already been sent.
	const inFlightBytesByChunk = new Map<number, number>();
	let totalInFlightBytes = 0;
	let progressCallbacksEnabled = true;
	for (const chunk of options.allChunks) {
		if (chunk.isUploaded && isValidEtag(chunk.etag)) {
			options.parts.push({
				ETag: chunk.etag,
				PartNumber: chunk.chunkIndex + 1
			});
		}
	}

	const reportProgress = (): void => {
		if (!options.onProgress || !progressCallbacksEnabled || options.cancelSignal?.aborted) return;
		options.onProgress(totalBytesUploaded + totalInFlightBytes);
	};
	const setInFlightBytes = (chunkIndex: number, bytes: number): void => {
		const previous = inFlightBytesByChunk.get(chunkIndex) ?? 0;
		if (bytes === previous) return;
		totalInFlightBytes += bytes - previous;
		if (bytes === 0) inFlightBytesByChunk.delete(chunkIndex);
		else inFlightBytesByChunk.set(chunkIndex, bytes);
	};
	reportProgress();

	const chunkSource = options.chunkSources.forPersistedUpload(options.session);
	const pendingChunksByIndex = new Map(options.pendingChunks.map((chunk) => [chunk.chunkIndex, chunk]));
	const pendingIndexes = options.pendingChunks.map((chunk) => chunk.chunkIndex);
	const remainingChunkIndexes = new Set(pendingIndexes);
	let chunksCompleted = 0;
	let lastProgressLeaseTouchAt = Date.now();
	let activeWorkLeaseTouchInFlight: Promise<void> | undefined;
	let activeWorkLeaseTouchStartedAt: number | undefined;
	let activeWorkLeaseTouchesEnabled = true;
	let activeWorkOwnershipLost = false;
	const stopProgressForOwnershipLoss = (): UploadOwnershipLostError => {
		progressCallbacksEnabled = false;
		inFlightBytesByChunk.clear();
		totalInFlightBytes = 0;
		return new UploadOwnershipLostError();
	};
	let activeLeaseProtectedWorkCount = 0;
	const reportHeartbeatOwnershipDecision = (event: string, chunkIndex: number, error?: unknown): void => {
		try {
			options.telemetry.logWarn(
				event,
				{
					sessionId: options.session.id,
					conversationId: options.session.conversationId,
					chunkIndex
				},
				error
			);
		} catch {
			// Diagnostics cannot replace the upload or ownership outcome.
		}
	};
	const updateActiveLeaseProtectedWorkCount = (isActive: boolean): void => {
		activeLeaseProtectedWorkCount += isActive ? 1 : -1;
		if (activeLeaseProtectedWorkCount < 0) {
			reportHeartbeatOwnershipDecision("recording.fileUpload.leaseActivityCounterUnderflow", -1);
			activeLeaseProtectedWorkCount = 0;
		}
	};
	const activeWorkAbortController = new AbortController();
	const abortActiveWorkForCancellation = () => activeWorkAbortController.abort();
	const abortActiveWorkForOwnershipLoss = () => {
		stopProgressForOwnershipLoss();
		activeWorkOwnershipLost = true;
		activeWorkAbortController.abort();
	};
	if (options.cancelSignal?.aborted) {
		abortActiveWorkForCancellation();
	} else {
		options.cancelSignal?.addEventListener("abort", abortActiveWorkForCancellation, { once: true });
	}
	const reportLeaseTouchError = (error: unknown, chunkIndex: number): void => {
		try {
			options.onLeaseTouchError?.(error, chunkIndex);
		} catch {
			// A diagnostic callback cannot replace the upload or ownership outcome.
		}
	};
	const touchLeaseForActiveWork = (chunkIndex = -1): void => {
		if (!activeWorkLeaseTouchesEnabled) return;
		if (activeWorkLeaseTouchInFlight) {
			try {
				options.telemetry.logWarn("recording.fileUpload.leaseHeartbeatSkipped", {
					sessionId: options.session.id,
					conversationId: options.session.conversationId,
					chunkIndex,
					inFlightMs: Math.max(0, Date.now() - (activeWorkLeaseTouchStartedAt ?? Date.now()))
				});
			} catch {
				// Diagnostics cannot replace the upload or ownership outcome.
			}
			return;
		}
		try {
			activeWorkLeaseTouchStartedAt = Date.now();
			activeWorkLeaseTouchInFlight = (async () => {
				try {
					await options.lease.touch(options.session);
				} catch (error) {
					reportLeaseTouchError(error, chunkIndex);
				}
				try {
					// Admin's durable lease records a failed renewal as non-ownership and resolves touch(),
					// so the heartbeat must prove ownership itself before allowing a stalled PUT to continue.
					if (!(await options.lease.owns(options.session))) {
						reportHeartbeatOwnershipDecision("recording.fileUpload.leaseOwnershipLostDuringHeartbeat", chunkIndex);
						abortActiveWorkForOwnershipLoss();
					}
				} catch (error) {
					reportLeaseTouchError(error, chunkIndex);
					reportHeartbeatOwnershipDecision("recording.fileUpload.leaseOwnershipUnreadableDuringHeartbeat", chunkIndex, error);
					// A stalled PUT cannot safely continue when we cannot establish that it still owns the
					// durable writer lease. Yielding preserves the persisted session for a later claimant.
					abortActiveWorkForOwnershipLoss();
				}
			})()
				.finally(() => {
					activeWorkLeaseTouchInFlight = undefined;
					activeWorkLeaseTouchStartedAt = undefined;
				});
		} catch (error) {
			activeWorkLeaseTouchStartedAt = undefined;
			reportLeaseTouchError(error, chunkIndex);
		}
	};
	// Progress callbacks are not a liveness clock. Safari can leave a healthy or eventually retryable
	// XHR pending without emitting progress for longer than the durable writer TTL. Keep the lease
	// alive for exactly the lifetime of active chunk work; renewal still cannot reacquire an expired
	// or replaced token, so a genuine successor remains authoritative.
	const activeWorkLeaseHeartbeat =
		pendingIndexes.length > 0
			? globalThis.setInterval(() => {
				// A parked offline/retry wait is resumable work, not an active upload operation. Let its
				// durable writer lease expire so a later drain can reclaim the persisted session.
				if (activeLeaseProtectedWorkCount > 0) touchLeaseForActiveWork();
			}, UPLOAD_PROGRESS_LEASE_TOUCH_INTERVAL_MS)
			: undefined;

	try {
		await processWithAdaptiveConcurrency(
			pendingIndexes,
			new AimdConcurrencyController(options.chunkConcurrency, options.telemetry, {
				sessionId: options.session.id,
				conversationId: options.session.conversationId
			}),
			async (chunkIndex, _workerIndex, markDurableProgress) => {
				const chunkMetadata = pendingChunksByIndex.get(chunkIndex);
				if (!chunkMetadata) {
					throw new Error(`Missing metadata for pending chunk ${chunkIndex}`);
				}
				try {
					await options.lease.touch(options.session);
				} catch (error) {
					reportLeaseTouchError(error, chunkIndex);
				}
				if (!(await options.lease.owns(options.session))) {
					throw stopProgressForOwnershipLoss();
				}

				let lastAttemptBytesUploaded = 0;
				const putChunk = (): Promise<string> =>
					uploadChunk({
						fileName: options.session.fileName,
						sessionId: options.session.id,
						conversationId: options.session.conversationId,
						chunkIndex,
						expectedByteLength: chunkMetadata.size,
						presignedUrl: options.chunkUrls[chunkIndex],
						headers: options.chunkHeaders?.[chunkIndex],
						maxRetries: options.maxChunkRetries,
						chunkSource,
						transport: options.transport,
						telemetry: options.telemetry,
						cancelSignal: activeWorkAbortController.signal,
						refreshPresignedUrl: options.refreshPresignedUrl
							? async (expiredUrl, reason) => ({
									url:
										reason === undefined
											? await options.refreshPresignedUrl!(chunkIndex, expiredUrl, [...remainingChunkIndexes])
											: await options.refreshPresignedUrl!(chunkIndex, expiredUrl, [chunkIndex], reason),
									headers: options.chunkHeaders?.[chunkIndex]
								})
							: undefined,
						createChunkSourceError: options.createChunkSourceError
							? (kind) => options.createChunkSourceError!(kind, options.session.fileName)
							: undefined,
						isOnline: options.isOnline,
						waitForOnline: options.waitForOnline,
						isPresignedUrlExpired: options.isPresignedUrlExpired,
						retryDelay: options.retryDelay,
						ensureOwnership: async () => {
							if (!(await options.lease.owns(options.session))) throw stopProgressForOwnershipLoss();
						},
						ensureOwnershipAfterRead: options.ensureOwnershipAfterRead
							? async () => {
									if (!(await options.lease.owns(options.session))) throw stopProgressForOwnershipLoss();
								}
							: undefined,
						onAttemptStart: () => {
							lastAttemptBytesUploaded = 0;
							if (inFlightBytesByChunk.has(chunkIndex)) {
								setInFlightBytes(chunkIndex, 0);
								reportProgress();
							}
						},
						onTransportPutActivityChange: (isActive) => {
							updateActiveLeaseProtectedWorkCount(isActive);
						},
						onProgress: (bytesUploaded) => {
							if (!progressCallbacksEnabled || !activeWorkLeaseTouchesEnabled || activeWorkAbortController.signal.aborted) return;
							if (!Number.isFinite(bytesUploaded) || bytesUploaded <= lastAttemptBytesUploaded) return;
							lastAttemptBytesUploaded = bytesUploaded;
							setInFlightBytes(chunkIndex, Math.min(bytesUploaded, chunkMetadata.size));
							reportProgress();
							options.onActivity?.();
							const now = Date.now();
							if (now - lastProgressLeaseTouchAt < UPLOAD_PROGRESS_LEASE_TOUCH_INTERVAL_MS) return;
							lastProgressLeaseTouchAt = now;
							touchLeaseForActiveWork(chunkIndex);
						}
					});

				/**
				 * LSP-19201: a 2xx PUT whose ETag we could not read is not a failed upload — the bytes are on
				 * S3 and only the receipt is missing, so re-PUTting reproduces the loss instead of repairing
				 * it (production: 509 events, one recording, six days, always the same part). Ask the server
				 * for the receipt it already holds.
				 *
				 * Scoped to MissingEtagError alone, and reached only after the retry budget: every other
				 * failure class either genuinely needs a re-PUT or is already rethrown before the loop's final
				 * throw (see `isPermanentChunkError` in uploadChunk.ts). A real transport failure must never
				 * arrive here, or a part S3 REJECTED could be "recovered" from a manifest entry left behind by
				 * an earlier attempt.
				 */
				let etag: string;
				try {
					etag = await putChunk();
				} catch (error) {
					// A sibling can still finish during the adaptive pool's failure grace window.
					// Its progress must not include bytes from this exhausted PUT attempt.
					if (inFlightBytesByChunk.has(chunkIndex)) {
						setInFlightBytes(chunkIndex, 0);
						if (activeWorkLeaseTouchesEnabled && !activeWorkAbortController.signal.aborted) reportProgress();
					}
					if (activeWorkOwnershipLost && !options.cancelSignal?.aborted) {
						try {
							options.telemetry.logWarn(
								"recording.fileUpload.leaseOwnershipLostDuringChunk",
								{
									sessionId: options.session.id,
									conversationId: options.session.conversationId,
									chunkIndex
								},
								error
							);
						} catch {
							// Diagnostics cannot replace the yielded ownership-loss outcome.
						}
						throw stopProgressForOwnershipLoss();
					}
					if (!(error instanceof MissingEtagError || (options.session.contractVersion === 2 && error instanceof S3HttpError && error.status === 412)) || !options.recoverPartReceipt) throw error;
					// The port logs every one of its own refusals by name; this catch exists only for a throw it
					// could not report itself (a custom port, or its telemetry throwing). Left bare it would make
					// a failed repair indistinguishable from a repair that never ran, which is the invisibility
					// this ticket exists to remove — see CLAUDE.md "Diagnostic logging when fixing silent failures".
					//
					// try/catch rather than `.catch()` on the returned promise (ls-heroku-ci, LOW, PR #4582):
					// `.catch()` only handles a REJECTED promise, and the port's type
					// `(input) => Promise<string | null>` also permits a non-async function that throws
					// SYNCHRONOUSLY. Such a throw happens before `.catch()` is ever attached, escapes this
					// enclosing catch block (a catch cannot catch its own body), and leaves uploadPendingChunks
					// as a bare Error — which classifyFailure routes to "other" (release to pending AND rethrow)
					// instead of LSP-18742's non-destructive retry-slow tier. That is the module's stated
					// invariant inverted: a repair handing back a WORSE verdict than the failure it was
					// repairing. Both in-repo callers use the async createPartReceiptRecovery factory, so this
					// only ever bit an out-of-tree consumer — but the comment above claimed the coverage either
					// way, and a try/catch simply makes the claim true for both throw shapes.
					let recovered: string | null;
					try {
						recovered = await options.recoverPartReceipt({
							chunkIndex,
							partNumber: chunkIndex + 1,
							expectedPartSize: chunkMetadata.size,
							totalChunks: options.session.totalChunks,
							expectedTargetId: pinnedUploadsTargetIdForPart(options.session, chunkIndex + 1),
							error
						});
					} catch (recoveryError) {
						if (isConfirmedMigrationIntegrityError(recoveryError)) throw recoveryError;
						try {
							options.telemetry.logWarn(
								"recording.fileUpload.partReceiptRecoveryThrew",
								{
									sessionId: options.session.id,
									conversationId: options.session.conversationId,
									chunkIndex,
									partNumber: chunkIndex + 1
								},
								recoveryError
							);
						} catch {
							// Telemetry must never turn a recoverable upload into a thrown one.
						}
						recovered = null;
					}
					// Adjudicate cancel and ownership BEFORE the recovery verdict, so BOTH exits from this block
					// report them. Two awaits stand between the failed PUT and here — the probe and this
					// ownership read — and a cancel or a lost lease can land during either. Resolve ownership
					// first so both awaits are behind us, then decide.
					//
					// Break-it round 1 (D17) caught the probe window and round 8 the ownership-read window, but
					// both fixes sat on the SUCCESS exit only; Bugbot (PR #4582) caught the asymmetry. Guarding
					// one of two paired branches is its own contradiction: a cancelled or superseded upload
					// whose recovery ALSO failed was reported as MissingEtagError and classified `retry-slow`,
					// so the cancel never reached the driver's tombstone path and a superseded writer released
					// a row it no longer owned instead of quietly yielding.
					//
					// Cancellation outranks a lost lease deliberately. A cancelled upload must surface as
					// UploadCancelledError, which the driver turns into its cancel/tombstone path; reporting a
					// lost lease instead makes the loop answer "yielded" and the cancel goes unobserved.
					const ownsAfterRecovery = await options.lease.owns(options.session);
					if (options.cancelSignal?.aborted) {
						throw new UploadCancelledError(`Upload of "${options.session.fileName}" was cancelled`);
					}
					if (!ownsAfterRecovery) throw stopProgressForOwnershipLoss();
					// Only now the recovery's own verdict. Rethrowing the ORIGINAL error, not a
					// recovery-specific one: LSP-18742's classifyFailure routes MissingEtagError to the
					// non-destructive slow retry tier, and a different class would fall through to a worse
					// verdict for a session whose bytes are all still intact.
					//
					// Re-validated here rather than trusted from the port: the in-repo factory already refuses a
					// blank server ETag, but the port type is `string | null`, and an out-of-tree port answering
					// `""` would seed `parts` and the durable checkpoint with an ETag that
					// `hasValidMultipartParts` later refuses — turning a recoverable receipt into the truncated-
					// parts terminal verdict. Guard where the value is CONSUMED, not only where it is produced.
					if (!isValidEtag(recovered)) throw error;
					etag = recovered;
				}

				try {
					// The PUT is necessarily outside IndexedDB, but its durable ETag checkpoint
					// must validate the writer token inside the same app transaction as the
					// chunk-meta write. `runGuardedEffect` uses that atomic path when the app
					// lease provides it; the legacy owns() path remains for compatibility.
					const checkpointOutcome = await runGuardedEffect(options.lease, options.session, () =>
						options.checkpointChunk(chunkIndex, etag)
					);
					if (checkpointOutcome === "skipped-not-owned") {
						throw stopProgressForOwnershipLoss();
					}
				} catch (error) {
					if (error instanceof UploadOwnershipLostError) {
						stopProgressForOwnershipLoss();
						throw error;
					}
					options.onCheckpointError?.(error, chunkIndex, etag);
				}
				if (!(await options.lease.owns(options.session))) {
					throw stopProgressForOwnershipLoss();
				}
				// The ETag checkpoint is now durable/observable. Credit the adaptive pool before
				// running the remaining in-memory progress callbacks so a concurrent transient
				// failure is requeued rather than treated as the first failure.
				markDurableProgress();

				const chunkByteLength = chunkMetadata.size;
				setInFlightBytes(chunkIndex, 0);
				chunkBytesUploaded[chunkIndex] = chunkByteLength;
				totalBytesUploaded += chunkByteLength;
				remainingChunkIndexes.delete(chunkIndex);
				chunksCompleted++;
				options.onChunkComplete?.({
					chunkIndex,
					chunkSize: chunkByteLength,
					chunksCompleted,
					totalPendingChunks: pendingIndexes.length
				});
				reportProgress();
				options.parts.push({ ETag: etag, PartNumber: chunkIndex + 1 });
			}
		);
	} catch (error) {
		if (error instanceof UploadOwnershipLostError) {
			// A worker can discover ownership loss outside the heartbeat (for example, at its pre-PUT
			// ownership check). Stop any sibling PUTs before yielding this writer's session.
			abortActiveWorkForOwnershipLoss();
			// Silent-failure rule: this converts a thrown ownership loss into an ordinary-looking
			// "yielded" outcome that the admin drain maps to RESUME_OUTCOME.RETRY and renders as
			// "Finishing in the background…". With no log here, a resume that gave up before its first
			// byte was indistinguishable from one that never started — the exact reconstruction the
			// 2026-08-31 stall investigation had to do by elimination.
			console.warn("[Upload] Chunk loop yielded: lease ownership lost before a chunk could be sent", {
				sessionId: options.session.id,
				uploadId: options.session.uploadId,
				chunksCompleted
			});
			return { outcome: "yielded", chunksCompleted };
		}
		// The adaptive pool returns after its bounded grace window for a retryable network verdict;
		// sibling PUTs may still be completing and need to read their ETag or recover a missing receipt.
		// A permanent verdict, by contrast, ends this attempt and must stop sibling work before the
		// caller releases its lease.
		if (!(error instanceof ChunkNetworkError)) activeWorkAbortController.abort();
		throw error;
	} finally {
		progressCallbacksEnabled = false;
		// A retryable sibling may finish its PUT and recover an ETag after the pool returns, but it must
		// not start a new durable renewal after this scope hands its token back to the caller.
		activeWorkLeaseTouchesEnabled = false;
		if (activeWorkLeaseHeartbeat !== undefined) globalThis.clearInterval(activeWorkLeaseHeartbeat);
		options.cancelSignal?.removeEventListener("abort", abortActiveWorkForCancellation);
		// Do not let a final renewal land after the caller releases this scope. Admin releases the
		// current token exactly; returning while this promise can still rotate that token would turn a
		// clean release into a stale-token no-op and strand the writer until its TTL.
		await activeWorkLeaseTouchInFlight;
		options.parts.sort((left, right) => left.PartNumber - right.PartNumber);
	}
	if (activeWorkOwnershipLost) {
		// The final heartbeat can discover a lost durable token after the last PUT completed. Do not
		// report that upload as successful: the caller must yield and let the current writer reconcile.
		console.warn("[Upload] Chunk loop yielded: lease ownership lost during final heartbeat", {
			sessionId: options.session.id,
			uploadId: options.session.uploadId,
			chunksCompleted
		});
		return { outcome: "yielded", chunksCompleted };
	}

	return { outcome: "uploaded", chunksCompleted };
}
